import { createPortal } from "react-dom";
import {
  Check,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Link2,
  LoaderCircle,
  Sparkles,
  X
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/shared/api/api-client";
import { influencerTier, publisherLabel, reportRefLabel, themeLabel, toMention, type Mention } from "@/shared/types/mention";
import { LinkMentionDialog } from "./LinkMentionDialog";
import { MentionCenaSummary } from "./mention-cena-detail";
import { APPROACH_STARS, ApproachStars } from "./mention-classification";
import { classificationMethodLabel, clippingLabel, describeOrigin } from "./mention-origin";
import { hasPlatformLogo, PlatformLogo } from "@/shared/components/PlatformLogo";
import { MentionClassificationHistory } from "./MentionClassificationEditor";
import { MentionInlineCatalog, type MentionCatalog } from "./MentionInlineCatalog";
import { MentionQuickClassify } from "./MentionQuickClassify";
import { mentionPlatform } from "@/shared/components/mention-platform";
import { SentimentBadge } from "@/shared/components/SentimentBadge";
import { formatCurrencyCompact } from "@/shared/utils/format-currency";
import { HighlightedText } from "@/shared/components/HighlightedText";
import { GenerateSummary, SummaryText } from "./MentionSummary";
import { useMentionSummary } from "./use-mention-summary";

import { useDismiss } from "@/shared/hooks/useDismiss";
/**
 * Detalhe da menção: texto inteiro, CENA, o porquê da IA e a trilha de
 * edição. Desde 2026-10-19 é só leitura (mais revisar, vincular e
 * descartar): a classificação se edita na linha — card e Modo revisão —,
 * e o formulário em pop-up que morava aqui saiu.
 *
 * **Exceção (2026-10-22): aberto pela visão Publicações, ele edita.** Lá a
 * linha não é uma menção e não tem os controles do card, então o detalhe é
 * onde a classificação se corrige. Quem pede isso é quem abre, passando
 * `loadCatalog` (o catálogo da marca da menção); sem ele nada muda. Os
 * controles são os mesmos do card (`MentionQuickClassify` e
 * `MentionInlineCatalog`).
 *
 * A listagem só traz um trecho do texto: matéria inteira ×100 por página
 * seria uma resposta absurda — no dado real há matéria de 38 mil
 * caracteres. Então o drawer **busca o detalhe** (`GET /api/mentions/{id}`)
 * ao abrir, e o texto completo entra quando chega. O trecho que veio da
 * lista aparece na hora, sem tela em branco.
 *
 * O `reload()` segue sendo chamado depois de revisar, vincular e
 * descartar: mostrar o estado novo ao lado da nota velha seria pior que
 * recarregar.
 */
type MentionDrawerProps = {
  mention: Mention;
  token: string;
  close: () => void;
  /** Avisa quem abriu que a menção MUDOU (correção salva, revisão
   *  marcada) — é o que deixa a lista por trás atualizar a linha em vez
   *  de ficar com o valor velho até um F5. Com `mentionId`, só aquela
   *  menção mudou e a lista a atualiza no lugar (2026-11-03); sem ele
   *  (vincular/mover cria ou tira linha), a lista refaz a página. */
  onChanged?: (mentionId?: number) => void;
  /** Presente = drawer EDITÁVEL (aberto pela visão Publicações). O
   *  catálogo só é pedido quando alguém abre um "+". */
  loadCatalog?: (mention: Mention) => Promise<MentionCatalog>;
  /** Só leitura (o Visualizador, 2026-10-22): sem revisar nem vincular. */
  readOnly?: boolean;
};

// Acima disso o texto abre recolhido — 38 mil caracteres empurrariam o
// painel de CENA pra fora da tela e transformariam o drawer num scroll
// infinito.
const COLLAPSE_THRESHOLD = 1200;

export function MentionDrawer({ mention, token, close, onChanged, loadCatalog, readOnly = false }: MentionDrawerProps) {
  const platform = mentionPlatform(mention);
  const PlatformIcon = platform.Icon;
  const [detail, setDetail] = useState<Mention | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(true);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  // Muda depois de salvar, pra trilha recarregar sem o histórico
  // precisar saber que uma edição aconteceu.
  const [historyKey, setHistoryKey] = useState(0);
  const [reviewBusy, setReviewBusy] = useState(false);
  const [linking, setLinking] = useState(false);
  const [linkNotice, setLinkNotice] = useState<string | null>(null);
  // Falha de uma gravação da classificação: fica ao lado dos controles, sem
  // trocar o texto da matéria pelo erro (`detailError` é do carregamento).
  const [editError, setEditError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoadingDetail(true);
    try {
      setDetail(toMention(await apiClient.mentionDetail(token, mention.id)));
      setDetailError(null);
      setHistoryKey(key => key + 1);
    } catch (failure) {
      setDetailError(
        failure instanceof Error
          ? failure.message
          : "Não foi possível carregar o texto completo."
      );
    } finally {
      setLoadingDetail(false);
    }
  }, [token, mention.id]);

  // Marca (ou desmarca) como revisada sem abrir o editor: é o "olhei e
  // está certo". O detalhe recarrega (a trilha ganha uma linha) e quem
  // abriu o drawer é avisado, pra lista por trás atualizar o selo.
  const toggleReviewed = async () => {
    if (!detail) return;
    setReviewBusy(true);
    try {
      await apiClient.setMentionReviewed(
        token,
        mention.id,
        detail.reviewedAt === null
      );
      onChanged?.(mention.id);
      await reload();
    } catch (failure) {
      setDetailError(
        failure instanceof Error
          ? failure.message
          : "Não foi possível mudar a revisão."
      );
    } finally {
      setReviewBusy(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setDetailError(null);
    setEditError(null);
    setLoadingDetail(true);
    setExpanded(false);
    apiClient.mentionDetail(token, mention.id)
      .then(result => { if (!cancelled) setDetail(toMention(result)); })
      .catch(failure => {
        if (!cancelled) {
          setDetailError(
            failure instanceof Error
              ? failure.message
              : "Não foi possível carregar o texto completo."
          );
        }
      })
      .finally(() => { if (!cancelled) setLoadingDetail(false); });
    return () => { cancelled = true; };
  }, [mention.id, token]);

  // Gravou: o detalhe recarrega (nota, trilha e chips novos) e a lista por
  // trás é avisada, pra não ficar com o valor velho.
  const handleSaved = () => {
    setEditError(null);
    onChanged?.(mention.id);
    void reload();
  };

  // Com o pop-up de vincular aberto, o Esc é DELE — senão fecharia os
  // dois de uma vez.
  useDismiss(!linking, () => close());

  // Enquanto o detalhe não chega, o excerpt da lista já preenche a tela.
  const shown = detail ?? mention;
  const untitled = shown.title === "Publicação sem título";
  const tier = shown.mediaType === "press" ? null : influencerTier(shown.followerCount);
  const origin = describeOrigin(shown);
  // Os termos chegam como a expressão gravada; aqui só viram chips.
  const terms = (shown.foundTerms ?? "")
    .split(/;|\s+OR\s+/)
    .map(term => term.trim().replace(/^"(.*)"$/, "$1"))
    .filter(Boolean);
  const editable = loadCatalog !== undefined;
  const fullText = detail?.excerpt ?? "";
  // O resumo da IA (2026-10-31): no detalhe aparece sempre que existe,
  // independente da preferência de leitura da lista.
  const summaryState = useMentionSummary(shown, token);
  const isLong = fullText.length > COLLAPSE_THRESHOLD;
  const body = !detail
    ? mention.excerpt
    : isLong && !expanded
      ? `${fullText.slice(0, COLLAPSE_THRESHOLD).trimEnd()}…`
      : fullText;

  return createPortal(
    <div className="modal-overlay" onClick={close}>
      <div
        className="modal-container drawer mention-detail-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mention-drawer-title"
        onClick={event => event.stopPropagation()}
      >
        <header className="mention-detail-head">
          <span className={`mention-detail-logo net-${platform.tone}`} title={platform.label}>
            {hasPlatformLogo(platform.tone)
              ? <PlatformLogo tone={platform.tone} size={18} mono/>
              : <PlatformIcon size={18} aria-hidden="true"/>}
          </span>
          <div className="mention-detail-origin">
            <strong>{shown.source}</strong>
            <span>
              {shown.brandName && <span className="mention-row-brand">{shown.brandName}</span>}
              <span>{shown.date}</span>
            </span>
          </div>
          {shown.originalLink && (
            <a className="mention-detail-open" href={shown.originalLink} target="_blank" rel="noreferrer">
              <ExternalLink size={14}/> Ver no site do veículo
            </a>
          )}
          {shown.link && (
            <a className="mention-detail-open" aria-label="Abrir na clipadora" href={shown.link} target="_blank" rel="noreferrer">
              <ExternalLink size={14}/> Abrir conteúdo
            </a>
          )}
          <button className="mention-detail-close" aria-label="Fechar detalhes" onClick={close}><X size={18}/></button>
        </header>

        <div className="drawer-body mention-detail-body">
          {!untitled && (
            <h2 id="mention-drawer-title">
              <HighlightedText text={shown.title} spans={shown.highlights.title} />
            </h2>
          )}
          {untitled && <h2 id="mention-drawer-title" className="sr-only">Menção #{mention.id}</h2>}

          {(shown.reviewedAt || shown.linkedAt || linkNotice || shown.discardedAt) && (
            <div className="mention-detail-flags" aria-live="polite">
              {shown.reviewedAt && (
                <span className="mention-detail-flag is-positive">
                  <Check size={13}/> Revisada{shown.reviewedByName ? ` por ${shown.reviewedByName}` : ""}
                </span>
              )}
              {shown.linkedAt && (
                <span className="mention-detail-flag">
                  <Link2 size={13}/> Vinculada à mão{shown.linkedByName ? ` por ${shown.linkedByName}` : ""}: a
                  esteira não casou esta publicação com a marca.
                </span>
              )}
              {linkNotice && <span className="mention-detail-flag">{linkNotice}</span>}
              {shown.discardedAt && (
                <span className="mention-detail-flag is-danger">
                  Descartada{shown.discardedByName ? ` por ${shown.discardedByName}` : ""}
                  {shown.discardReason ? ` · ${shown.discardReason}` : ""} — fora da
                  lista, do resumo e das amostras novas. Restaure pela lista
                  "Só as descartadas".
                </span>
              )}
            </div>
          )}

          {summaryState.summary !== null ? (
            <div className="mention-detail-summary">
              <SummaryText summary={summaryState.summary} origin={summaryState.origin} className="article-copy" />
              {!readOnly && !shown.discardedAt && (
                <GenerateSummary state={summaryState} label="Gerar de novo" />
              )}
            </div>
          ) : (
            !readOnly &&
            !shown.discardedAt && (
              <div className="mention-detail-summary">
                <GenerateSummary state={summaryState} />
              </div>
            )
          )}

          <article className={`mention-full-text ${isLong && !expanded ? "collapsed" : ""}`}>
            {/* Os trechos vieram pro texto inteiro; `HighlightedText` apara
                o que passa do corte quando a matéria está recolhida. Antes
                do detalhe chegar, o trecho da lista já vem com os seus. */}
            <p className="article-copy">
              <HighlightedText text={body} spans={shown.highlights.content} />
            </p>
          </article>
          {loadingDetail && (
            <p className="mention-text-status">
              <LoaderCircle className="spin" size={13}/>Carregando texto completo…
            </p>
          )}
          {detailError && (
            <p className="mention-text-status error">
              {detailError} — mostrando o trecho da listagem.
            </p>
          )}
          {isLong && (
            <button
              type="button"
              className="text-button mention-text-toggle"
              onClick={() => setExpanded(current => !current)}
            >
              {expanded
                ? <><ChevronUp size={14}/>Recolher texto</>
                : <><ChevronDown size={14}/>Ler matéria completa ({fullText.length.toLocaleString("pt-BR")} caracteres)</>}
            </button>
          )}

          {/* Alcance e valoração são ESPELHOS: seguidores só existem em
              rede, valoração só em imprensa. */}
          <div className="mention-detail-stats">
            <span>
              <small>{shown.mediaType === "press" ? "Valoração" : "Alcance"}</small>
              <strong>{shown.mediaType === "press" ? formatCurrencyCompact(shown.adValue) : shown.reach}</strong>
            </span>
            {tier && <span><small>Porte</small><strong>{tier}</strong></span>}
            {/* O código da clipadora — o que se confere contra a
                planilha e o relatório anterior. */}
            {shown.nativeId && <span><small>Cód. na clipadora</small><strong className="is-code">{shown.nativeId}</strong></span>}
          </div>

          <div className="mention-detail-columns">
            <section className="mention-detail-panel">
              <h3 className="mention-detail-panel-title"><Sparkles size={15}/> Classificação</h3>
              {editable && (
                <div className="mention-detail-edit">
                  <MentionQuickClassify
                    mention={shown}
                    token={token}
                    onSaved={handleSaved}
                    onError={setEditError}
                  />
                </div>
              )}
              {editError && (
                <p className="mention-text-status error" role="alert">{editError}</p>
              )}
              {(
                <dl className="mention-detail-facts-v2">
                  {!editable && (
                    <>
                      <div>
                        <dt>Polaridade</dt>
                        <dd><SentimentBadge value={shown.sentiment} polarity={shown.polarity}/></dd>
                      </div>
                      <div>
                        <dt>Abordagem</dt>
                        <dd className="mention-detail-approach">
                          <ApproachStars approach={shown.approach} stars={APPROACH_STARS[shown.approach] ?? 0} size={13}/>
                          <span>{shown.approach}</span>
                        </dd>
                      </div>
                      {/* `null` = não se aplica (rede, TV/rádio, imprensa sem
                          nota): o fato não aparece, como o controle do card. */}
                      {shown.titleCitation !== null && (
                        <div>
                          <dt>Citação no título</dt>
                          <dd>{shown.titleCitation ? "Sim" : "Não"}</dd>
                        </div>
                      )}
                    </>
                  )}
                  {publisherLabel(shown) && (
                    <div>
                      <dt>Publicador</dt>
                      <dd>
                        {shown.authorProfileUrl ? (
                          <a href={shown.authorProfileUrl} target="_blank" rel="noreferrer">
                            {publisherLabel(shown)}
                          </a>
                        ) : (
                          publisherLabel(shown)
                        )}
                      </dd>
                    </div>
                  )}
                  {loadCatalog ? (
                    // Os mesmos controles do card: × tira, "+" abre o
                    // catálogo da marca (só quando é aberto).
                    (
                      [
                        ["themes", "Subtemas", shown.themes],
                        ["keyMessages", "Mensagens-chave", shown.keyMessages],
                        ["spokespersons", "Porta-vozes", shown.spokespersons]
                      ] as const
                    ).map(([kind, label, items]) => (
                      <div key={kind}>
                        <dt>{label}</dt>
                        <dd>
                          <MentionInlineCatalog
                            kind={kind}
                            mention={shown}
                            token={token}
                            items={items}
                            loadOptions={() => loadCatalog(shown).then(catalog => catalog[kind])}
                            onSaved={handleSaved}
                            onError={setEditError}
                            locked={shown.discardedAt !== null}
                          />
                        </dd>
                      </div>
                    ))
                  ) : (
                    <>
                      {shown.themes.length > 0 && (
                        <div>
                          <dt>Subtemas</dt>
                          <dd className="mention-detail-chips">
                            {shown.themes.map(theme => (
                              <span key={theme.id} className="mention-term-chip">{themeLabel(theme)}</span>
                            ))}
                          </dd>
                        </div>
                      )}
                      {shown.keyMessages.length > 0 && (
                        <div>
                          <dt>Mensagens-chave</dt>
                          <dd>{shown.keyMessages.map(k => k.name).join(", ")}</dd>
                        </div>
                      )}
                      {shown.spokespersons.length > 0 && (
                        <div>
                          <dt>Porta-vozes</dt>
                          <dd>{shown.spokespersons.map(s => s.name).join(", ")}</dd>
                        </div>
                      )}
                    </>
                  )}
                  {shown.reports.length > 0 && (
                    <div>
                      <dt>Em amostras</dt>
                      <dd>{shown.reports.map(reportRefLabel).join(", ")}</dd>
                    </div>
                  )}
                  {shown.otherBrands.length > 0 && (
                    <div>
                      <dt>Também em</dt>
                      <dd>
                        {shown.otherBrands
                          .map(s => (s.isCompetitor ? `${s.brandName} (${s.companyName})` : s.brandName))
                          .join(", ")}
                      </dd>
                    </div>
                  )}
                </dl>
              )}
              <MentionClassificationHistory
                mentionId={mention.id}
                token={token}
                reloadKey={historyKey}
              />
            </section>
            <div className="mention-detail-aside">
              <MentionCenaSummary mention={shown}/>
              {terms.length > 0 && (
                <section className="mention-detail-panel is-compact">
                  <h3 className="mention-detail-panel-title">Termos que casaram</h3>
                  <div className="mention-term-chips">
                    {terms.map(term => <span key={term} className="mention-term-chip">{term}</span>)}
                  </div>
                </section>
              )}
            </div>
          </div>

          <p className="mention-details-meta">
            <span>Via {clippingLabel(shown.clippingService)}{shown.channel ? ` · ${shown.channel}` : ""}</span>
            <span>ID <code translate="no">{mention.id}</code></span>
            <span>{classificationMethodLabel(shown.classificationMethod)}</span>
            {origin && <span>{origin}</span>}
          </p>
        </div>

        {/* As ações do detalhe moram num rodapé fixo (2026-10-13): no
            título da seção elas se espremiam em três linhas. "Corrigir"
            continua esperando o detalhe — ver o cabeçalho do arquivo. */}
        {detail && !readOnly && (
          <footer className="mention-detail-actions">
            {detail.discardedAt && (
              <p className="form-hint">
                Menção descartada — restaure pela lista ou pela Lixeira pra editar.
              </p>
            )}
            <button
              type="button"
              className={`review-secondary${detail.reviewedAt ? " is-done" : ""}`}
              disabled={reviewBusy}
              onClick={() => void toggleReviewed()}
            >
              <Check size={14}/>
              {detail.reviewedAt ? "Desmarcar revisão" : "Marcar como revisada"}
            </button>
            {!detail.discardedAt && (
              <button type="button" className="review-secondary" onClick={() => setLinking(true)}>
                <Link2 size={14}/> Vincular a outra marca
              </button>
            )}
          </footer>
        )}
      </div>
      {linking && (
        // O clique do pop-up não pode chegar ao fundo do drawer, que
        // fecha ao ser clicado.
        <div onClick={event => event.stopPropagation()}>
          <LinkMentionDialog
            token={token}
            mentionIds={[mention.id]}
            sourceBrandId={shown.brandId}
            onClose={() => setLinking(false)}
            onDone={message => {
              setLinking(false);
              setLinkNotice(message);
              // A lista por trás ganha a menção nova (e perde esta, se
              // foi "mover"); o detalhe recarrega o "também em".
              onChanged?.();
              void reload();
            }}
          />
        </div>
      )}
    </div>,
    document.body
  );
}
